import { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Input, Button } from '../../../components/ui';
import { useAuth } from '../../../context/AuthContext';
import { SignUpFormProps, SignUpData } from '../../../types';
import UserTypeSelector from './UserTypeSelector';

const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess, onSwitchToSignIn, defaultUserType = 'student' }) => {
  const [formData, setFormData] = useState<SignUpData>({
    email: '',
    password: '',
    fullName: '',
    confirmPassword: '',
    userType: defaultUserType
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { signUp } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate form data
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match');
      }
      
      if (!formData.fullName.trim()) {
        throw new Error('Full name is required');
      }
      
      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }
      
      const { error } = await signUp({
        email: formData.email.trim(),
        password: formData.password,
        fullName: formData.fullName.trim(),
        userType: formData.userType
      });
      
      if (error) throw error;
      
      onSuccess(formData.email);
    } catch (err: any) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof SignUpData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleUserTypeChange = (userType: 'professor' | 'student') => {
    setFormData(prev => ({ ...prev, userType }));
    if (error) setError('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <UserTypeSelector
        value={formData.userType}
        onChange={handleUserTypeChange}
      />

      <Input
        type="text"
        label="Full Name"
        value={formData.fullName}
        onChange={(e) => handleInputChange('fullName', e.target.value)}
        placeholder="Enter your full name"
        required
        fullWidth
      />

      <Input
        type="email"
        label="Email Address"
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        placeholder="Enter your email address"
        required
        fullWidth
      />

      <Input
        type={showPassword ? 'text' : 'password'}
        label="Password"
        helperText="Minimum 6 characters"
        value={formData.password}
        onChange={(e) => handleInputChange('password', e.target.value)}
        placeholder="Enter your password"
        required
        fullWidth
        rightIcon={
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="text-gray-400 hover:text-gray-600"
          >
            {showPassword ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        }
      />

      <Input
        type={showConfirmPassword ? 'text' : 'password'}
        label="Confirm Password"
        value={formData.confirmPassword}
        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
        placeholder="Confirm your password"
        required
        fullWidth
        error={formData.confirmPassword && formData.password !== formData.confirmPassword ? 'Passwords do not match' : undefined}
        rightIcon={
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="text-gray-400 hover:text-gray-600"
          >
            {showConfirmPassword ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        }
      />

      <Button
        type="submit"
        loading={loading}
        fullWidth
        size="lg"
        disabled={formData.password !== formData.confirmPassword}
      >
        Create Account
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={onSwitchToSignIn}
          className="text-sm text-emerald-600 hover:text-emerald-500 font-medium transition-colors"
        >
          Already have an account? Sign in
        </button>
      </div>

      <div className="text-xs text-gray-500 text-center">
        By creating an account, you agree to our Terms of Service and Privacy Policy.
      </div>
    </form>
  );
};

export default SignUpForm; 